import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AllVotesReport } from "./AllVotes";
import styled, { useTheme } from "styled-components";
import { isNumericOrDollarAmount } from "../../../../components/Reports/ResultsTable";
import { getStandardDate } from "./manageHeaders";
import { CSVLink } from "react-csv";
import Button from "../../../../components/Button/Button";

const DualScrollWrapper = styled.div`
  width: 100%;

  .dual-scroll-top {
    overflow-x: auto;
    overflow-y: hidden;
    height: 20px;
  }

  .scrollbar-spacer {
    height: 1px;
  }

  .dual-scroll-content {
    overflow-x: auto;
    height: auto;
  }
`;

const AllVotesHeaders: { [key: string]: string } = {
  voteId: "Vote ID",
  entryId: "Entry ID",
  entryTitle: "Entry Title",
  brand: "Brand",
  entrantCompany: "Entrant Company",
  authorCompany: "Author Company",
  jurorId: "Juror ID",
  jurorName: "Juror Name",
  jurorCompany: "Juror Company",
  voteOptionId: "Vote Option ID",
  voteOption: "Vote Option",
  voteValue: "Vote Value",
  voteCastDate: "Vote Cast Date",
  batch: "Batch",
  entrySet: "Entry Set",
  juryId: "Jury ID",
  jury: "Jury",
  round: "Round",
  roundTypeId: "Round Type ID",
  totalScore: "Total Score",
};

interface AllVotesResultsTableProps {
  data: AllVotesReport[];
  disableExport?: boolean;
  reportName?: string;
  showTopScroll?: boolean;
}

interface DualScrollProps {
  children: React.ReactNode;
  showTopScroll?: boolean;
}

const DualScroll: React.FC<DualScrollProps> = ({
  children,
  showTopScroll = true,
}) => {
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const [contentScrollWidth, setContentScrollWidth] = useState(0);

  useLayoutEffect(() => {
    const topScrollElement = topScrollRef.current;
    const contentScrollElement = contentScrollRef.current;

    const syncScroll = (
      source: HTMLDivElement | null,
      target: HTMLDivElement | null
    ) => {
      if (source && target) {
        target.scrollLeft = source.scrollLeft;
      }
    };

    const handleTopScroll = () => {
      syncScroll(topScrollElement, contentScrollElement);
    };

    const handleContentScroll = () => {
      syncScroll(contentScrollElement, topScrollElement);
    };

    topScrollElement?.addEventListener("scroll", handleTopScroll);
    contentScrollElement?.addEventListener("scroll", handleContentScroll);

    return () => {
      topScrollElement?.removeEventListener("scroll", handleTopScroll);
      contentScrollElement?.removeEventListener("scroll", handleContentScroll);
    };
  }, []);

  useLayoutEffect(() => {
    if (contentScrollRef.current) {
      setContentScrollWidth(contentScrollRef.current.scrollWidth);
    }
  }, [children]);

  return (
    <DualScrollWrapper>
      {showTopScroll && (
        <div className="dual-scroll-top" ref={topScrollRef}>
          <div
            className="scrollbar-spacer"
            style={{ width: `${contentScrollWidth}px` }}
          ></div>
        </div>
      )}
      <div className="dual-scroll-content" ref={contentScrollRef}>
        {children}
      </div>
    </DualScrollWrapper>
  );
};

const AllVotesResultsTable: React.FC<AllVotesResultsTableProps> = ({
  data,
  disableExport = false,
  reportName = "",
  showTopScroll,
}) => {
  const theme = useTheme();
  const [csvData, setCsvData] = useState<any[]>([]);

  const columnKeysAndHeaders = Object.keys(data[0] || {}).map(
    (key) => [key, AllVotesHeaders[key] || key] as [string, string]
  );

  useEffect(() => {
    setCsvData(data.map((entry) => ({ ...entry })));
  }, [data]);

  const csvHeaders = Object.keys(AllVotesHeaders).map((key) => ({
    label: AllVotesHeaders[key],
    key: key,
  }));

  return (
    <>
      <DualScroll showTopScroll={showTopScroll}>
        <table className="text-left table-auto border-collapse w-full text-sm search-table">
          <thead className="bg-colorBackgroundLight">
            <tr className="border-b border-gray-300">
              {columnKeysAndHeaders.map(([key, header], index) => {
                const backgroundColor =
                  index === 0
                    ? theme.colorBackgroundMedium
                    : theme.colorBackgroundLight;
                const textColor =
                  index === 0 ? theme.colorCopyLightLight : "initial";

                let sampleContent: string | number | boolean | null | undefined;
                for (const row of data) {
                  const cellContent = row[key as keyof AllVotesReport];
                  if (
                    cellContent !== null &&
                    cellContent !== undefined &&
                    typeof cellContent !== "boolean" &&
                    cellContent !== ""
                  ) {
                    sampleContent = cellContent;
                    break;
                  }
                }

                const isContentNumericOrDollar =
                  sampleContent !== undefined &&
                  sampleContent !== null &&
                  typeof sampleContent !== "boolean" &&
                  isNumericOrDollarAmount(sampleContent);

                return (
                  <th
                    key={header}
                    style={{
                      backgroundColor,
                      color: textColor,
                      textAlign: isContentNumericOrDollar ? "right" : "left",
                    }}
                    className={`p-3 ${
                      index === 0
                        ? "bg-colorBackgroundDarkDark text-colorCopyLightLight"
                        : "bg-colorBackgroundLight"
                    }`}
                  >
                    {header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-colorBackgroundLightLight divide-y divide-gray-200">
            {data.map((result, rowIndex) => (
              <tr className="border-b border-gray-300" key={rowIndex}>
                {columnKeysAndHeaders.map(([key], colIndex) => {
                  let cellContent = result[key as keyof AllVotesReport];

                  if (typeof cellContent === "boolean") {
                    cellContent = cellContent ? "True" : "False";
                  }

                  const isContentNumericOrDollar =
                    (typeof cellContent === "string" ||
                      typeof cellContent === "number") &&
                    isNumericOrDollarAmount(cellContent);

                  const textAlignClass = isContentNumericOrDollar
                    ? "text-right"
                    : "text-left";
                  const cellFontClass = isContentNumericOrDollar
                    ? "font-mono"
                    : "";

                  return (
                    <td
                      key={colIndex}
                      className={`p-3 ${textAlignClass} ${cellFontClass}`}
                    >
                      {cellContent}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </DualScroll>

      {!disableExport && (
        <div className="py-5 w-5">
          <CSVLink
            className="no-underline text-colorCopyLightLight"
            data={csvData}
            headers={csvHeaders}
            filename={`${reportName || "AllVotes"}_${getStandardDate()}.csv`}
          >
            <Button>Export to CSV</Button>
          </CSVLink>
        </div>
      )}
    </>
  );
};

export default AllVotesResultsTable;
